const MODEL_OUTAGE_KEYWORDS: readonly string[] = [
  "error status code",
  "server problem",
  "service unavailable",
  "temporarily unavailable",
  "overloaded",
  "overload",
  "bad gateway",
  "gateway timeout",
  "try again later",
  "something went wrong",
  "an error has occurred",
  "too many requests",
  "502",
  "503",
  "unexpected end of json input",
];

type StreamErrorCause = StreamErrorLike | string | null | undefined;

type StreamErrorLike = {
  code?: string;
  message?: string;
  error?: string;
  status?: number;
  response?: {
    status?: number;
  };
  cause?: StreamErrorCause;
};

const isAbortLikeError = (message: string) => {
  const lowerMessage = message.toLowerCase();
  return lowerMessage.includes("cancel") || lowerMessage.includes("abort");
};

const isStreamErrorLike = (value: StreamErrorCause): value is StreamErrorLike => typeof value === "object" && value !== null;
const hasStatusResponse = (
  value: StreamErrorLike["response"]
): value is {
  status?: number;
} => typeof value === "object" && value !== null;

const getCandidateStatusCode = (error: StreamErrorCause): number | null => {
  if (!isStreamErrorLike(error)) return null;

  if (typeof error.status === "number") return error.status;
  if (hasStatusResponse(error.response) && typeof error.response.status === "number") return error.response.status;

  if (error.cause) {
    return getCandidateStatusCode(error.cause);
  }

  return null;
};

export const MODEL_OUTAGE_MESSAGE_PARTS = {
  prefix: "It's not you, it's us. We're on it. This usually clears up quickly, but you can ",
  updatesLinkText: "visit our updates channel",
  connector: " or ",
  supportLinkText: "contact support",
  suffix: ". Oh, and don't worry—this didn't use any credits.",
};

export const MODEL_OUTAGE_MESSAGE = `${MODEL_OUTAGE_MESSAGE_PARTS.prefix}${MODEL_OUTAGE_MESSAGE_PARTS.updatesLinkText}${MODEL_OUTAGE_MESSAGE_PARTS.connector}${MODEL_OUTAGE_MESSAGE_PARTS.supportLinkText}${MODEL_OUTAGE_MESSAGE_PARTS.suffix}`;

export const getErrorString = (error: StreamErrorCause): string => {
  if (typeof error === "string") {
    return error;
  }

  if (!isStreamErrorLike(error)) {
    return "";
  }

  if (typeof error.message === "string" && error.message.trim()) {
    return error.message;
  }

  if (typeof error.error === "string" && error.error.trim()) {
    return error.error;
  }

  if (error.cause) {
    return getErrorString(error.cause);
  }

  return "";
};

// Admission owns this code and its busy/refresh message. Do not expose raw
// provider diagnostics from other errors in the generation notice.
export const getSprintErrorMessage = (error: StreamErrorCause): string | null => {
  if (!isStreamErrorLike(error)) return null;
  if (error.code === "NOVEL_NOVEMBER_SPRINT_UNAVAILABLE") {
    return getErrorString(error).trim() || "Free sprint access is unavailable. Please refresh and try again.";
  }
  return error.cause ? getSprintErrorMessage(error.cause) : null;
};

export const isProbableModelOutageError = (error: StreamErrorCause) => {
  if (!error) {
    return false;
  }

  const status = getCandidateStatusCode(error);
  // Sprint admission uses 503 for capacity, eligibility and window refusals.
  // Preserve its retry/refresh instructions instead of claiming a model outage.
  if (getSprintErrorMessage(error)) {
    return false;
  }
  // Enrollment and configured usage limits are actionable application errors,
  // even when their message contains phrases such as "temporarily unavailable".
  if (isStreamErrorLike(error) && error.code === "benchmark_unavailable" && status !== null && status >= 400 && status < 500) {
    return false;
  }
  if (typeof status === "number" && status >= 500) {
    return true;
  }

  const messages = [];
  const message = getErrorString(error);
  if (message) {
    messages.push(message.toLowerCase());
  }
  if (isStreamErrorLike(error) && error.cause) {
    const causeMessage = getErrorString(error.cause);
    if (causeMessage) {
      messages.push(causeMessage.toLowerCase());
    }
  }

  if (messages.length === 0) {
    return false;
  }

  if (messages.some((msg) => isAbortLikeError(msg))) {
    return false;
  }

  return messages.some((msg) => MODEL_OUTAGE_KEYWORDS.some((keyword) => msg.includes(keyword)));
};
